import { useState } from 'react';
import type { InAppNotification } from '../hooks/useFollowupNotifications';

interface NotificationCenterProps {
  onNotificationClick?: (leadId: string) => void | Promise<void>;
  notifications: InAppNotification[];
  markAsRead: (notificationId: string) => void;
  clearAll: () => void;
}

export default function NotificationCenter({
  onNotificationClick,
  notifications,
  markAsRead,
  clearAll,
}: NotificationCenterProps) {
  const [isOpen, setIsOpen] = useState(false);

  const visibleNotifications = notifications.filter(n => !n.read);
  const unreadCount = visibleNotifications.length;

  const formatTime = (date: Date) => {
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    return date.toLocaleDateString();
  };

  const handleNotificationClick = (notification: InAppNotification) => {
    markAsRead(notification.id);
    setIsOpen(false);

    if (notification.leadStatus?.trim().toLowerCase() === 'lost') return;

    if (notification.leadId && onNotificationClick) {
      void onNotificationClick(notification.leadId);
    }
  };

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 rounded-full hover:bg-slate-700/60 transition-colors"
      >
        <span className="text-xl">🔔</span>
        {unreadCount > 0 && (
          <span className="absolute top-0 right-0 bg-red-500 text-white text-[10px] font-bold rounded-full w-5 h-5 flex items-center justify-center">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {isOpen && (
        <>
          <div
            className="fixed inset-0 bg-black/50 z-40"
            onClick={() => setIsOpen(false)}
          />
          <div className="fixed left-2 right-2 top-16 z-50 mx-auto flex max-h-[calc(100vh-5rem)] w-[calc(100vw-1rem)] max-w-80 flex-col overflow-hidden rounded-lg bg-white shadow-xl sm:left-auto sm:right-4 sm:w-80">
            <div className="p-3 border-b border-slate-200 flex items-center justify-between">
              <h3 className="font-semibold text-slate-800">Notifications</h3>
              <button
                onClick={() => {
                  clearAll();
                  setIsOpen(false);
                }}
                className="text-xs text-red-600 hover:text-red-700"
              >
                Clear All
              </button>
            </div>
            
            {visibleNotifications.length === 0 ? (
              <div className="p-6 text-center text-slate-500 text-sm">
                No notifications yet
              </div>
            ) : (
              <div className="flex-1 overflow-y-auto">
                {visibleNotifications.map((notification) => (
                  <div
                    key={notification.id}
                    onClick={() => handleNotificationClick(notification)}
                    className={`cursor-pointer border-b border-slate-100 px-3 py-3 transition-colors hover:bg-slate-50 ${
                      notification.read ? 'bg-white' : 'bg-slate-50/60'
                    }`}
                  >
                    <div className="flex items-start gap-2">
                      <span className="mt-0.5 text-lg">
                        {notification.type === 'followup' ? '📅' : '🔔'}
                      </span>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-sm text-slate-800">
                          {notification.title}
                        </div>
                        <div className="text-xs text-slate-600 mt-1 line-clamp-2">
                          {notification.message}
                        </div>
                        <div className="text-[10px] text-slate-400 mt-1">
                          {formatTime(notification.timestamp)}
                        </div>
                      </div>
                      {!notification.read && (
                        <div className="mt-2 h-2 w-2 flex-shrink-0 rounded-full bg-blue-500" />
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
